<script setup>
import { computed, toRefs } from 'vue';
import { useImage } from '@vueuse/core';
import useSizeByBounds from './composables/use-size-by-bounds';
import { assetPath } from './utils/path';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const scaleKonvaConfig = config => ({
  ...config,
  x: config.x * scaleFactor.value,
  y: config.y * scaleFactor.value,
  width: config.width * scaleFactor.value,
  height: config.height * scaleFactor.value,
});

const TARGET_PERCENTAGE = 0.6;
const POSITION_X_PERCENTAGE = 0.5;
const POSITION_Y_PERCENTAGE = 0.04;

const props = defineProps({
  stageBoundingBox: {
    type: Object,
    required: true,
  },
  inputBoundingBox: {
    type: Object,
    required: true,
  },
  scaleFactor: {
    type: Number,
    required: true,
  },
  src: {
    type: String,
    required: true,
  },
  // the target size of the sticker, in percent
  targetSize: {
    type: Number,
    default: 0.6,
  },
  // relative position of the sticker from the center, in percent
  defaultPosition: {
    type: Object,
    default: () => ({
      x: 0.5,
      y: 0.5,
    }),
  },
});

const {
  stageBoundingBox,
  inputBoundingBox,
  scaleFactor,
  src,
  defaultPosition,
  targetSize,
} = toRefs(props);

const { state: stickerImage } = useImage({
  src: src.value,
});

const { size: inputSize } = useSizeByBounds(inputBoundingBox);

const stickerScale = computed(() => {
  if (!stickerImage.value) return 1;

  const idealHeight = inputSize.value.height * targetSize.value;
  const idealWidth = inputSize.value.width * targetSize.value;

  const stickerWidth = stickerImage.value.width;
  const stickerHeight = stickerImage.value.height;

  const widthRatio = idealWidth / stickerWidth;
  const heightRatio = idealHeight / stickerHeight;
  const minRatio = Math.min(widthRatio, heightRatio);

  return minRatio;
});

const stickerPosition = computed(() => {
  if (!stickerImage.value) return {};

  const inputCenterX = 0.5 * inputSize.value.width;
  const inputCenterY = 0.5 * inputSize.value.height;

  const stickerWidth = stickerImage.value.width;
  const stickerHeight = stickerImage.value.height;
  const stickerRatio = stickerWidth / stickerHeight;

  const width = stickerWidth * stickerScale.value;
  const height = width / stickerRatio;

  const x = inputCenterX - defaultPosition.value.x * width;
  const y = inputCenterY - defaultPosition.value.y * height;

  return {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
  };
});
const stickerHeight = computed(() => {
  return stickerPosition.value.bottom - stickerPosition.value.top;
});
const stickerWidth = computed(() => {
  return stickerPosition.value.right - stickerPosition.value.left;
});

const configSticker = computed(() => {
  return {
    x: stickerPosition.value.left,
    y: stickerPosition.value.top,
    width: stickerWidth.value,
    height: stickerHeight.value,
    image: stickerImage.value,
    draggable: true,
    dragBoundFunc: position => {
      return {
        x: clamp(
          position.x,
          0,
          (stageBoundingBox.value.right - stickerWidth.value) *
            scaleFactor.value
        ),
        y: clamp(
          position.y,
          0,
          (stageBoundingBox.value.bottom - stickerHeight.value) *
            scaleFactor.value
        ),
      };
    },
  };
});
</script>
<template>
  <v-image v-if="stickerImage" :config="scaleKonvaConfig(configSticker)" />
  <!-- <v-text
    :config="{
      text: `
       originalWidth: ${stickerImage?.width} * ${stickerScale}
       width: ${configSticker.width} * ${scaleFactor}
       scaledWidth ${scaleKonvaConfig(configSticker).width}`,
      fill: 'white',
      fontSize: 12,
    }"
  /> -->
</template>
